/**
 * NOVA PROPOSTA, EDITAR PROPOSTA e EDITAR CONTRATO — em passo a passo desde
 * 18/09/2026.
 *
 * > *"Podemos deixar o mesmo layout/visual desse formulário que fizemos em
 * > imóveis, para todos os outros formulários que o HUB tiver?"* — Richard,
 * > 16/09/2026. É o terceiro da fila (`docs/FORMULARIOS-E-LINK-DO-CLIENTE.md`,
 * > A.4), depois do cliente, com as mesmas peças (`PassoAPasso.tsx`).
 *
 * ⛔ **Muda o desenho, não o que o botão faz.** Continua igual:
 *
 * - **Todo contrato nasce PROPOSTA** — forma de pagamento, vencimentos e
 *   parcelas nascem no Aceitar, nunca aqui.
 * - **Escolher o nível repõe o honorário** pelo de referência (só na proposta).
 * - **O total é a soma das pesquisas** e não se digita.
 * - **Cortesia** some com honorário, fundo e assinatura.
 * - **Casos só no contrato ativo**; marcar transfere, desmarcar solta.
 * - **Gravar pergunta antes** e **fechar com alteração pendente avisa**.
 * - O que se grava é o mesmo, pela mesma rota.
 *
 * ⚠️ **No Editar, todos os passos abrem de uma vez e "Salvar" vale de qualquer
 * um** — quem edita veio mudar um campo, não refazer a proposta.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { Valor } from './Valor.tsx';
import { AjudaDoPasso, CampoDoPasso, EscolhaDoNivel, PassosDoTopo, Progresso, ResumoDoPasso, type MarcaDoCampo } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import type { Caso, Contrato, DadosContrato, NivelServico } from '../lib/api.ts';
import { apiContratos, type ReferenciaDoNivel } from '../lib/api.ts';
import { NIVEIS } from '../lib/rotulos.ts';
import { paraTela } from '../lib/datas.ts';
import { completarReais, digitarReais, mascararDinheiro, paraApi } from '../lib/dinheiro.ts';
import type { PropostaDoPedido } from './PedidosDoCliente.tsx';
import '../estilos/proposta-2026.css';

type Props = {
  clienteId: string;
  clienteNome: string;
  /** Os casos deste cliente, para marcar quais o contrato engloba. */
  casos: Caso[];
  /** Preenchido quando se está corrigindo um contrato já existente. */
  inicial?: Contrato | null;
  /** Já vem marcado quando o contrato nasce a partir de um caso. */
  casoInicial?: string | null;
  /**
   * ⭐ **A proposta montada a partir de um PEDIDO DE PESQUISA — 17/09/2026.**
   * As pesquisas nascem com o nível e os investigados que o cliente escreveu;
   * o honorário vem da referência do nível, como ao clicar nele. O servidor
   * tira o pedido de "a conferir" na mesma gravação.
   */
  doPedido?: PropostaDoPedido | null;
  aoFechar: () => void;
  aoSalvar: (dados: DadosContrato) => Promise<void>;
};

/** Uma pesquisa em edição, como a tela a guarda: tudo texto, como o usuário digita. */
type PesquisaNaTela = {
  nivel: NivelServico;
  valor: string;
  /** Vazio = o teto do nível. Preenchido só quando se combinou outro número. */
  investigados: string;
  /** ⚠️ QUEM esta pesquisa investiga. Não confundir com `investigados`, que é quantos. */
  entendimento: string;
  /** O que o cliente escreveu no pedido: enquanto o texto for esse, a barra roxa diz de onde veio. */
  doPedido?: string;
  /** ⭐ 29/09/2026 · só o Customizado: o escopo, o prazo em dias e as reuniões do briefing. */
  escopo: string;
  prazoDias: string;
  reunioes: string;
};

const NOVA: PesquisaNaTela = { nivel: 'n2_forensic', valor: '', investigados: '', entendimento: '', escopo: '', prazoDias: '', reunioes: '' };

/** O Customizado sem os três campos do briefing não vira proposta (o servidor também recusa). */
const faltaOBriefing = (p: PesquisaNaTela) =>
  p.nivel === 'customizado' && (!p.escopo.trim() || !Number(p.investigados) || !Number(p.prazoDias) || p.reunioes === '');

/**
 * `"1200.00"` da API → `"1.200,00"` na tela.
 *
 * `null` vira vazio: o campo em branco diz "não preenchido", que é diferente
 * de zero.
 */
function emReaisDeTela(valor: string | null | undefined): string {
  if (!valor) return '';
  return mascararDinheiro(valor.replace('.', ''));
}

/**
 * A soma das pesquisas, no formato de tela.
 *
 * ⚠️ **Soma em CENTAVOS inteiros, e não em reais com vírgula.** `0,1 + 0,2` não
 * dá `0,3` em ponto flutuante, e erro de centavo em contrato é assunto sério —
 * é a mesma razão pela qual o servidor usa `Decimal`.
 */
function somaDasPesquisas(pesquisas: PesquisaNaTela[]): string {
  const centavos = pesquisas.reduce((t, p) => {
    const cru = paraApi(p.valor);
    return t + (cru ? Math.round(Number(cru) * 100) : 0);
  }, 0);
  return centavos === 0 ? '' : mascararDinheiro(String(centavos));
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

export function FormularioContrato({
  clienteId,
  clienteNome,
  casos,
  inicial = null,
  casoInicial = null,
  doPedido = null,
  aoFechar,
  aoSalvar,
}: Props) {
  const editando = inicial !== null;
  /**
   * ⚠️ **Não é escolha: todo contrato nasce PROPOSTA** — decisão do Richard,
   * 15/08/2026: *"Sempre inicia como proposta, após o aceite, vira contrato
   * ativo"*. O negócio fechado fora do Hub cria-se como proposta e aceita-se
   * em seguida: o aceite é onde entram assinatura, forma de pagamento e
   * vencimentos, que é tudo o que ele tem a mais.
   */
  const ehProposta = inicial ? inicial.ehProposta : true;
  const ativo = !ehProposta;

  const [marcados, setMarcados] = useState<string[]>(() => {
    if (inicial) return casos.filter((c) => c.contrato?.id === inicial.id).map((c) => c.id);
    return casoInicial ? [casoInicial] : [];
  });
  /**
   * ⭐ **O QUE O CLIENTE ESTÁ CONTRATANDO — 28/08/2026.** Uma linha por
   * pesquisa vendida: *"contratou uma pesquisa nível Insight e uma pesquisa
   * nível Kairós"*. Substituiu o seletor de nível único, que obrigava a
   * escolher qual dos dois o contrato "é".
   */
  const [pesquisas, setPesquisas] = useState<PesquisaNaTela[]>(
    inicial?.itens?.length
      ? inicial.itens.map((i) => ({
          nivel: i.nivel,
          valor: emReaisDeTela(i.valor),
          investigados: i.investigadosCombinado ? String(i.investigados ?? '') : '',
          entendimento: i.entendimento ?? '',
          escopo: i.escopo ?? '',
          prazoDias: i.prazoDias ? String(i.prazoDias) : '',
          reunioes: i.reunioes !== null && i.reunioes !== undefined ? String(i.reunioes) : '',
        }))
      : doPedido?.pesquisas.length
        ? doPedido.pesquisas.map((p) => ({ ...NOVA, nivel: p.nivel, entendimento: p.entendimento, doPedido: p.entendimento }))
        : [NOVA],
  );
  /**
   * ⭐⭐ **CORTESIA — 10/09/2026.** *"Precisa ter um checkbox sutil e
   * minimalista."* ⛔ Não é desconto de 100%: é ausência de contraprestação.
   * Some o valor, some o fundo e some a exigência de aceite — e o servidor
   * recusa cortesia com qualquer um dos três, porque a garantia mora lá.
   */
  const [cortesia, setCortesia] = useState(inicial?.cortesia ?? false);
  const [fundoDeCustas, setFundo] = useState(emReaisDeTela(inicial?.fundoDeCustas));
  const [validade, setValidade] = useState(String(inicial?.validadeDias ?? 30));
  const [condicoes, setCondicoes] = useState(inicial?.condicoesPagamento ?? '');
  /* ⚠️ Carregar do contrato não é conforto, é evitar perda de dado: estes
     campos nasciam vazios na edição e eram enviados assim — corrigir um
     contrato apagava a assinatura, que é de onde o prazo do caso conta. */
  const [assinadoEm, setAssinado] = useState(inicial?.assinadoEm ?? '');
  const [observacao, setObservacao] = useState(inicial?.observacao ?? '');
  /*
   * ⚠️ **O TOTAL NÃO SE DIGITA — 28/08/2026.** Ele é a soma das pesquisas, e o
   * servidor recusa contrato em que os dois não fecham. Some a possibilidade,
   * some o erro; a trava do servidor continua lá, guardando a API.
   */
  const valor = somaDasPesquisas(pesquisas);

  const passos = ['As pesquisas', 'Investigados', 'Condições', ...(ativo ? ['Casos'] : []), editando ? 'Conferir e salvar' : 'Conferir e gravar'];
  const ULTIMO = passos.length - 1;
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(editando ? ULTIMO : 0);
  const [removendo, setRemovendo] = useState<number | null>(null);

  const [referencia, setReferencia] = useState<ReferenciaDoNivel[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  useEffect(() => {
    apiContratos.referencia().then((r) => setReferencia(r.niveis)).catch(() => undefined);
  }, []);
  const ref = (n: NivelServico) => referencia.find((r) => r.id === n);

  /* ⭐ Do pedido: com a referência na mão, o honorário de cada pesquisa (e o
     fundo, pela primeira) entra como entraria ao clicar no nível. Uma vez só,
     e só no que está vazio — o que a pessoa já digitou fica. */
  const preencheuDoPedido = useRef(false);
  useEffect(() => {
    if (!doPedido || preencheuDoPedido.current || referencia.length === 0) return;
    preencheuDoPedido.current = true;
    setPesquisas((atual) =>
      atual.map((p) => {
        const r = referencia.find((x) => x.id === p.nivel);
        return !p.valor && r?.honorarios ? { ...p, valor: mascararDinheiro(r.honorarios.replace('.', '')) } : p;
      }),
    );
    const primeiro = referencia.find((x) => x.id === doPedido.pesquisas[0]?.nivel);
    if (primeiro?.fundoDeCustas) setFundo((f) => f || mascararDinheiro(primeiro.fundoDeCustas!.replace('.', '')));
  }, [doPedido, referencia]);

  /* ⚠️ `cortesia` entra aqui: sem ela, marcar a caixa e fechar a janela perde
     a escolha sem o aviso de alteração pendente. */
  const guarda = usarFormulario({ marcados, pesquisas, cortesia, fundoDeCustas, validade, condicoes, assinadoEm, observacao }, aoFechar);
  usarDialogo(guarda.fechar);

  /** Muda um campo de UMA pesquisa, deixando as outras onde estão. Mexeu, o aviso de erro sai. */
  function mexerNaPesquisa(pos: number, mudanca: Partial<PesquisaNaTela>) {
    setErro(null);
    setPesquisas((atual) => atual.map((p, i) => (i === pos ? { ...p, ...mudanca } : p)));
  }

  /**
   * Escolher o nível de uma pesquisa repõe o honorário dela pelo de referência.
   *
   * Sobrescreve o que estava digitado, e é o certo: quem troca de nível está
   * mudando o serviço, não corrigindo um número.
   *
   * ⚠️ **Depois do aceite o honorário NÃO acompanha o nível.** As parcelas já
   * existem e somam com o total — repor o valor de referência faria o contrato
   * dizer R$ 25.000 e as cobranças somarem R$ 4.000.
   *
   * ⚠️ **O teto de investigados NÃO é preenchido aqui** (28/08): vazio já
   * significa "o teto do nível", e o servidor o resolve lendo `lib/niveis.ts`.
   */
  function escolherNivelDaPesquisa(pos: number, n: NivelServico) {
    const r = ref(n);
    mexerNaPesquisa(pos, {
      nivel: n,
      ...(ehProposta && r?.honorarios ? { valor: mascararDinheiro(r.honorarios.replace('.', '')) } : {}),
    });
    /* O Fundo de Custas é do contrato, não da pesquisa: só a primeira o repõe,
       para duas pesquisas não somarem dois fundos sem ninguém pedir. */
    if (ehProposta && pos === 0 && r?.fundoDeCustas) {
      setFundo(mascararDinheiro(r.fundoDeCustas.replace('.', '')));
    }
  }

  /**
   * O que impede de sair do passo — e só os dois primeiros têm obrigatório.
   *
   * ⛔ **Toda pesquisa precisa de honorário (fora a cortesia) — 18/09/2026.**
   * A gravação descarta a pesquisa sem honorário: antes, com duas pesquisas e
   * a segunda sem valor, a proposta era gravada só com a primeira, e os
   * investigados escritos na segunda iam embora sem aviso.
   */
  function problemaDoPasso(i: number): string | null {
    if (i === 0 && !cortesia && pesquisas.some((p) => !paraApi(p.valor))) {
      return pesquisas.length > 1 ? 'Informe o honorário de cada pesquisa contratada.' : 'Informe o honorário da pesquisa.';
    }
    if (i === 0 && ehProposta && pesquisas.some(faltaOBriefing)) {
      return 'Escreva o escopo, o número de investigados, o prazo em dias e o número de reuniões da pesquisa Customizado, definidos no briefing.';
    }
    /* A proposta vale para estas pessoas, e não para outras: com duas
       pesquisas, faltar numa delas deixaria metade do contrato sem
       delimitação. No contrato já ativo o que foi escrito continua valendo. */
    if (i === 1 && ehProposta && pesquisas.some((p) => !p.entendimento.trim())) {
      return pesquisas.length > 1 ? 'Escreva quem será investigado em cada pesquisa contratada.' : 'Escreva quem será investigado.';
    }
    return null;
  }

  function irPara(p: number) {
    setErro(null);
    setPasso(p);
  }
  function avancar() {
    const x = problemaDoPasso(passo);
    if (x) return setErro(x);
    setAlcancado((a) => Math.max(a, passo + 1));
    irPara(passo + 1);
  }
  function pedirParaGravar() {
    if (emVoo.current) return;
    for (const i of [0, 1]) {
      const x = problemaDoPasso(i);
      if (x) {
        setPasso(i);
        return setErro(x);
      }
    }
    // A gravação só acontece depois do "sim". Ver lib/formulario.ts.
    guarda.pedirParaSalvar();
  }

  /**
   * O que aparece no cartão, na busca e no Financeiro.
   *
   * ⛔⛔ **NÃO É MAIS O NOME DO INVESTIGADO — 17/09/2026.** Era a primeira linha
   * dos investigados, e a busca global e a linha de cada parcela do
   * Financeiro — telas abertas com gente ao lado — mostravam nome e documento.
   * ⭐ Passa a ser o que foi **vendido** — "N1 · INSIGHT + N3 · KAIRÓS".
   *
   * ⚠️ **Contrato que já existe mantém o que está escrito.** Deduzir por cima
   * apagaria o registro do que se digitou.
   */
  function objetoDeduzido(): string {
    if (inicial?.objeto) return inicial.objeto;
    const niveis = [...new Set(pesquisas.map((p) => p.nivel))];
    return niveis.length > 0 ? niveis.map((n) => NIVEIS[n].longo).join(' + ') : 'CONTRATO';
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({
        clienteId,
        ...(doPedido && !inicial ? { pedidoId: doPedido.id } : {}),
        objeto: objetoDeduzido(),
        casoIds: ehProposta ? [] : marcados,
        /* ⛔ Cortesia zera o total: não há o que cobrar, e o servidor recusa
           qualquer outro número aqui. */
        valorTotal: cortesia ? '0.00' : paraApi(valor),
        cortesia,
        /* Proposta não tem cobrança: a forma, as parcelas e os vencimentos
           nascem no Aceitar (`AceitarProposta`). Estes três vão como sempre
           foram, para o servidor receber o mesmo corpo de antes. */
        formaPagamento: 'a_vista',
        entrada: null,
        intervalo: 'mensal',
        situacao: ehProposta ? 'proposta' : 'ativo',
        /* ⛔ Na cortesia nenhuma pesquisa tem honorário, e filtrar por ele
           apagaria TODAS — o contrato chegaria sem dizer o que foi contratado. */
        itens: pesquisas
          .filter((p) => cortesia || paraApi(p.valor))
          .map((p) => ({
            nivel: p.nivel,
            valor: cortesia ? '0.00' : paraApi(p.valor)!,
            investigados: Number(p.investigados) || null,
            entendimento: p.entendimento.trim() || null,
            ...(p.nivel === 'customizado'
              ? { escopo: p.escopo.trim() || null, prazoDias: Number(p.prazoDias) || null, reunioes: p.reunioes === '' ? null : Number(p.reunioes) }
              : {}),
          })),
        /* ⛔ Cortesia não tem fundo: o desembolso é da KAIRÓS. */
        /* ⭐ 29/09/2026: a cortesia leva o fundo como a paga. */
        fundoDeCustas: paraApi(fundoDeCustas) || null,
        validadeDias: Number(validade) || 30,
        condicoesPagamento: condicoes.trim() || null,
        /* ⛔ Cortesia não assina: não há contrato a firmar. */
        assinadoEm: assinadoEm || null,
        observacao: observacao.trim() || null,
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  const doPedidoAinda = (p: PesquisaNaTela): MarcaDoCampo => (p.doPedido !== undefined && p.entendimento === p.doPedido ? 'veio' : undefined);
  const tetoDe = (p: PesquisaNaTela) => (p.investigados ? Number(p.investigados) : ref(p.nivel)?.investigados ?? null);
  const chaveDaAjuda = (['pesquisas', 'investigados', 'condicoes', 'casos'] as const)[passo];
  const ajuda = passo < ULTIMO && chaveDaAjuda ? AJUDA[chaveDaAjuda] : undefined;
  const contexto: Contexto = { ehProposta, ativo, cortesia, doPedido: pesquisas.some((p) => doPedidoAinda(p)) };

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div
        className="imv cad-conferir pap-ficha prp-ficha"
        role="dialog"
        aria-modal="true"
        aria-label={editando ? `Editar ${ehProposta ? 'proposta' : 'contrato'}` : 'Nova proposta'}
      >
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">
                Ficha do cliente · {editando ? `editar ${ehProposta ? 'proposta' : 'contrato'}` : 'nova proposta'} · passo {passo + 1} de {passos.length}
              </span>
              <h3 className="cad-titulo">
                {editando ? (
                  <>
                    Editar {ehProposta ? 'proposta' : 'contrato'} <span className="pap-titulo-codigo">{inicial.codigo}</span>
                  </>
                ) : (
                  'Nova proposta'
                )}
              </h3>
              <p className="cad-sub">
                <b>{clienteNome}</b> ·{' '}
                {editando
                  ? 'Todos os passos estão abertos: mude o que precisar e salve de qualquer um deles.'
                  : 'Nasce como proposta. Quando o cliente aceitar, o botão Aceitar a transforma em contrato, sem redigitar nada.'}
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <PassosDoTopo passos={passos} atual={passo} alcancado={alcancado} aoIr={irPara} />

          {doPedido && !inicial && (
            <div className="cad-chegada ped-faixa-proposta">
              <Icone nome="caixa" tamanho={18} />
              <span>
                <b>
                  A partir do pedido de{' '}
                  {new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(doPedido.recebidoEm))}
                </b>{' '}
                · {doPedido.quem} ·{' '}
                {doPedido.pesquisas.length === 1 ? 'a pesquisa com os investigados já escritos' : `${doPedido.pesquisas.length} pesquisas com os investigados já escritos`}. O
                honorário veio da tabela do nível: confira.
              </span>
            </div>
          )}

          <div className="imv-corpo">
            <div className="imv-conteudo">
              {passo === 0 && (
                <>
                  {/* ⚠️ A cortesia fica ANTES das pesquisas, de propósito: ela muda o
                      que os campos abaixo significam. Depois deles, a pessoa
                      preencheria valores para vê-los desaparecerem. */}
                  <label className="prp-cortesia">
                    <input
                      type="checkbox"
                      checked={cortesia}
                      onChange={(e) => {
                        setErro(null);
                        setCortesia(e.target.checked);
                      }}
                    />
                    <span>
                      <b>Cortesia</b> — os honorários são cortesia e não geram cobrança. O Fundo de Custas, a assinatura e o aceite seguem o rito normal, e o caso
                      é trabalho igual: prazo, investigados e relatório seguem o nível.
                    </span>
                  </label>

                  <ol className="prp-pesquisas">
                    {pesquisas.map((p, pos) => {
                      const r = ref(p.nivel);
                      return (
                        <li key={pos} className={`prp-pesquisa pap-${p.nivel}`}>
                          <header className="prp-pesquisa-topo">
                            <span className="prp-pesquisa-n">Pesquisa {pos + 1}</span>
                            {/* ⚠️ A última pesquisa não se remove: contrato sem nenhuma
                                não gera proposta. */}
                            {pesquisas.length > 1 && (
                              <button type="button" className="imv-x" aria-label={`Remover a pesquisa ${pos + 1}`} title="Remover esta pesquisa" onClick={() => setRemovendo(pos)}>
                                <Icone nome="lixeira" tamanho={14} />
                              </button>
                            )}
                          </header>
                          <EscolhaDoNivel nivel={p.nivel} rotulo={`Nível da pesquisa ${pos + 1}`} aoEscolher={(n) => escolherNivelDaPesquisa(pos, n)} />
                          <p className="pap-clausula">
                            <LinhaDaReferencia nivel={p.nivel} r={r} />
                          </p>
                          <div className="imv-grade-campos">
                            {/* ⛔ CORTESIA: o honorário SOME, não fica zerado — campo
                                zerado é campo que se preenche. */}
                            {!cortesia && (
                              <CampoDoPasso rotulo="Honorário" obrigatorio col={2} mono>
                                <i className="imv-pre">R$</i>
                                <input
                                  id={`pesquisa-valor-${pos}`}
                                  inputMode="decimal"
                                  value={p.valor}
                                  onChange={(e) => mexerNaPesquisa(pos, { valor: digitarReais(e.target.value) })}
                                  onBlur={(e) => mexerNaPesquisa(pos, { valor: completarReais(e.target.value) })}
                                  placeholder="0,00"
                                />
                              </CampoDoPasso>
                            )}
                            <CampoDoPasso
                              rotulo="Investigados"
                              obrigatorio={ehProposta && p.nivel === 'customizado'}
                              col={2}
                              mono
                              nota={
                                p.nivel === 'customizado'
                                  ? 'Quantos, definidos no briefing.'
                                  : r?.investigados
                                    ? `Em branco, vale o teto do nível: ${r.investigados}.`
                                    : 'Em branco, vale o teto do nível.'
                              }
                            >
                              <input
                                id={`pesquisa-inv-${pos}`}
                                inputMode="numeric"
                                value={p.investigados}
                                onChange={(e) => mexerNaPesquisa(pos, { investigados: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                                placeholder={r?.investigados ? String(r.investigados) : ''}
                              />
                            </CampoDoPasso>
                            {/* ⭐ 29/09/2026: o Customizado não tem escopo, prazo nem reuniões
                                de nível. Vêm do briefing, e a proposta os imprime. */}
                            {p.nivel === 'customizado' && (
                              <>
                                <CampoDoPasso rotulo="Prazo" obrigatorio={ehProposta} col={2} mono nota="Em dias corridos, definido no briefing.">
                                  <input
                                    id={`pesquisa-prazo-${pos}`}
                                    inputMode="numeric"
                                    value={p.prazoDias}
                                    onChange={(e) => mexerNaPesquisa(pos, { prazoDias: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                  />
                                  <i className="imv-suf">dias</i>
                                </CampoDoPasso>
                                <CampoDoPasso rotulo="Reuniões" obrigatorio={ehProposta} col={2} mono nota="Quantas, definidas no briefing.">
                                  <input
                                    id={`pesquisa-reunioes-${pos}`}
                                    inputMode="numeric"
                                    value={p.reunioes}
                                    onChange={(e) => mexerNaPesquisa(pos, { reunioes: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                                  />
                                </CampoDoPasso>
                                <CampoDoPasso
                                  rotulo="Escopo definido no briefing"
                                  obrigatorio={ehProposta}
                                  col={6}
                                  nota="Um item por linha. Sai na proposta, e os honorários remuneram só o que estiver escrito aqui."
                                >
                                  <textarea
                                    id={`pesquisa-escopo-${pos}`}
                                    rows={Math.max(4, Math.min(12, p.escopo.split('\n').length + 1))}
                                    value={p.escopo}
                                    onChange={(e) => mexerNaPesquisa(pos, { escopo: e.target.value })}
                                    maxLength={8000}
                                  />
                                </CampoDoPasso>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                  <button type="button" className="imv-mais" onClick={() => setPesquisas((a) => [...a, NOVA])}>
                    <Icone nome="mais" tamanho={14} /> Acrescentar pesquisa
                  </button>

                  {cortesia ? (
                    <p className="prp-total prp-total-cortesia">
                      <b>Contrato de cortesia:</b> o total fica em zero. O nível continua valendo — é ele que define prazo, teto de investigados e as seções do
                      relatório.
                    </p>
                  ) : (
                    <div className="prp-total">
                      <span className="imv-rotulo">Honorários do contrato</span>
                      <strong>{paraApi(valor) ? <Valor de={paraApi(valor)} /> : <em>a preencher</em>}</strong>
                      <small>{pesquisas.length > 1 ? `a soma das ${pesquisas.length} pesquisas` : 'o honorário da pesquisa'}</small>
                    </div>
                  )}
                  {ativo && !cortesia && (
                    <p className="imv-pendencias">
                      <b>Contrato já aceito:</b> as cobranças existem e somam com o total. Dá para redistribuir entre as pesquisas sem mudar a soma; para mudar o
                      total, use <b>Corrigir cobranças</b> no cartão do contrato.
                    </p>
                  )}
                </>
              )}

              {passo === 1 && (
                <>
                  {contexto.doPedido && (
                    <p className="cad-legenda">
                      <span>
                        <i /> escrito pelo cliente no pedido — confira
                      </span>
                    </p>
                  )}
                  {pesquisas.map((p, pos) => {
                    const teto = tetoDe(p);
                    return (
                      <section key={pos} className={`prp-quem pap-${p.nivel}`}>
                        <header>
                          <span className={`selo-nivel selo-${p.nivel}`}>{NIVEIS[p.nivel].longo}</span>
                          <span className="prp-pesquisa-n">Pesquisa {pos + 1}</span>
                          {teto !== null && (
                            <span className="prp-teto">{p.investigados ? plural(teto, 'investigado combinado', 'investigados combinados') : `até ${plural(teto, 'investigado', 'investigados')}`}</span>
                          )}
                        </header>
                        <div className="imv-grade-campos">
                          <CampoDoPasso rotulo="Investigado(s) desta pesquisa" obrigatorio={ehProposta} col={6} marca={doPedidoAinda(p)} nota="Nome e documento, um por linha. Sai na proposta em PDF.">
                            {/* ⚠️ Não sobe para maiúsculas: é texto longo, e caixa alta
                                derruba a leitura (`CLAUDE.md`). */}
                            <textarea
                              id={`pesquisa-quem-${pos}`}
                              rows={Math.max(3, Math.min(8, p.entendimento.split('\n').length + 1))}
                              value={p.entendimento}
                              onChange={(e) => mexerNaPesquisa(pos, { entendimento: e.target.value })}
                              placeholder="FULANO DE TAL · CPF 000.000.000-00"
                              maxLength={4000}
                            />
                          </CampoDoPasso>
                        </div>
                      </section>
                    );
                  })}
                </>
              )}

              {passo === 2 && (
                <>
                  <div className="imv-grade-campos">
                    {/* ⭐ 29/09/2026: o Fundo de Custas aparece também na cortesia —
                        ela é só dos honorários, e as custas seguem o rito normal. */}
                    <CampoDoPasso
                      rotulo="Fundo de Custas"
                      col={2}
                      mono
                      nota={ativo ? 'Travado: as cobranças já existem.' : 'Vem da tabela do nível da primeira pesquisa.'}
                    >
                      <i className="imv-pre">R$</i>
                      <input
                        id="fundo"
                        inputMode="decimal"
                        value={fundoDeCustas}
                        onChange={(e) => setFundo(digitarReais(e.target.value))}
                        onBlur={(e) => setFundo(completarReais(e.target.value))}
                        placeholder="0,00"
                        disabled={!ehProposta}
                      />
                    </CampoDoPasso>
                    {ehProposta && (
                      <CampoDoPasso rotulo="Validade" col={1} mono>
                        <input id="validade" inputMode="numeric" value={validade} onChange={(e) => setValidade(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                        <i className="imv-suf">dias</i>
                      </CampoDoPasso>
                    )}
                    {/* ⭐ 29/09/2026: a cortesia de honorários ASSINA, como a paga —
                        é da assinatura que o prazo do caso conta. */}
                    {(
                      <CampoDoPasso
                        rotulo="Assinatura"
                        col={2}
                        mono
                        nota={ehProposta ? 'Opcional: o Aceitar também pede. O prazo do caso conta dela.' : 'O prazo do caso conta desta data.'}
                      >
                        <input id="assinado" type="date" value={assinadoEm} onChange={(e) => setAssinado(e.target.value)} />
                      </CampoDoPasso>
                    )}
                    {ehProposta && (
                      <CampoDoPasso
                        rotulo="Forma de pagamento no documento"
                        col={6}
                        nota={
                          cortesia
                            ? 'Em branco, a proposta traz a frase padrão: honorários em cortesia, sem cobrança, e o Fundo de Custas na assinatura.'
                            : 'Em branco, a proposta traz a frase padrão: 50% na assinatura mais o Fundo de Custas, e 50% na entrega.'
                        }
                      >
                        <input id="condicoes" value={condicoes} onChange={(e) => setCondicoes(e.target.value)} placeholder="Opcional" maxLength={500} />
                      </CampoDoPasso>
                    )}
                    <CampoDoPasso rotulo="Observação" col={6} nota="Fica só no Hub.">
                      <textarea id="obsct" rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={4000} />
                    </CampoDoPasso>
                  </div>
                  {cortesia && (
                    <p className="cad-nada">
                      <b>Cortesia de honorários.</b> As custas seguem o rito normal: o Fundo de Custas vai na proposta e é depositado na assinatura, com prestação de
                      contas e devolução do saldo. O prazo do caso conta da assinatura.
                    </p>
                  )}
                </>
              )}

              {ativo && passo === 3 && (
                <>
                  {casos.length > 0 ? (
                    <div className="pap-marcaveis">
                      {casos.map((c) => {
                        const doOutro = c.contrato && c.contrato.id !== inicial?.id;
                        const marcado = marcados.includes(c.id);
                        return (
                          <label key={c.id} className={`pap-marcavel${marcado ? ' on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={(e) => setMarcados((atual) => (e.target.checked ? [...atual, c.id] : atual.filter((x) => x !== c.id)))}
                            />
                            <span className={`selo-nivel selo-${c.nivel}`}>{NIVEIS[c.nivel].longo}</span>
                            <span className="pap-codigo">{c.codigo}</span>
                            <span className="pap-marcavel-texto">{c.titulo ?? ''}</span>
                            {doOutro && <span className="pap-marcavel-nota">hoje está em {c.contrato!.codigo}</span>}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="cad-nada">Este cliente ainda não tem caso.</p>
                  )}
                  <p className="imv-nota">Marcar um caso que está em outro contrato o transfere para este. Desmarcar solta o caso do contrato — nunca o apaga.</p>
                </>
              )}

              {passo === ULTIMO && (
                <div className="cad-resumo">
                  <section className="cad-bloco cad-resumo-inteiro">
                    <header>
                      <h6>{pesquisas.length > 1 ? `As ${pesquisas.length} pesquisas` : 'A pesquisa'}</h6>
                      <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => irPara(0)}>
                        Corrigir
                      </button>
                    </header>
                    <div className="prp-tabela" role="table">
                      <div className="prp-tl prp-tl-cab" role="row">
                        <span role="columnheader">Nível</span>
                        <span role="columnheader">Honorário</span>
                        <span role="columnheader">Investigados</span>
                      </div>
                      {pesquisas.map((p, pos) => (
                        <div key={pos} className="prp-tl" role="row">
                          <span role="cell">
                            <span className={`selo-nivel selo-${p.nivel}`}>{NIVEIS[p.nivel].longo}</span>
                          </span>
                          <span role="cell">{cortesia ? 'cortesia' : paraApi(p.valor) ? <Valor de={paraApi(p.valor)} /> : <i className="prp-falta">não informado</i>}</span>
                          <span role="cell" className="prp-mono">
                            {p.investigados ? `${p.investigados} · combinado` : tetoDe(p) !== null ? `até ${tetoDe(p)} · teto do nível` : 'definido no briefing'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className="cad-bloco cad-resumo-inteiro">
                    <header>
                      <h6>Investigados</h6>
                      <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => irPara(1)}>
                        Corrigir
                      </button>
                    </header>
                    <dl className="prp-quem-resumo">
                      {pesquisas.map((p, pos) => (
                        <div key={pos}>
                          <dt>
                            Pesquisa {pos + 1} · {NIVEIS[p.nivel].curto}
                          </dt>
                          <dd className={p.entendimento.trim() ? undefined : 'falta'}>{p.entendimento.trim() || 'não informado'}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                  <ResumoDoPasso
                    titulo="Condições"
                    aoCorrigir={() => irPara(2)}
                    linhas={[
                      ['Honorários', cortesia ? 'Cortesia — sem valor' : paraApi(valor) ? <Valor de={paraApi(valor)} /> : ''],
                      ['Fundo de Custas', paraApi(fundoDeCustas) ? <Valor de={paraApi(fundoDeCustas)} /> : ''],
                      ...(ehProposta
                        ? ([
                            ['Validade', `${Number(validade) || 30} dias`],
                            ['Pagamento', condicoes.trim() || (cortesia ? 'frase padrão: honorários em cortesia e o Fundo de Custas no aceite' : 'frase padrão: 50% no aceite mais o Fundo de Custas, e 50% na conclusão')],
                          ] as Array<[string, ReactNode]>)
                        : []),
                      ['Assinatura', assinadoEm ? paraTela(assinadoEm) : ''],
                      ['Observação', observacao.trim()],
                    ]}
                  />
                  {ativo ? (
                    <ResumoDoPasso
                      titulo="Casos"
                      aoCorrigir={() => irPara(3)}
                      linhas={[['Engloba', casos.filter((c) => marcados.includes(c.id)).map((c) => c.codigo).join(' · ')]]}
                    />
                  ) : (
                    <section className="cad-bloco prp-depois">
                      <header>
                        <h6>Depois de gravar</h6>
                      </header>
                      <p>
                        {cortesia
                          ? 'A proposta aparece nos contratos da ficha, com Proposta em PDF. No Aceitar entra a data da assinatura: os honorários são cortesia e não geram cobrança.'
                          : 'A proposta aparece nos contratos da ficha, com Proposta em PDF. No Aceitar entram a forma de pagamento e os vencimentos; os casos se vinculam depois do aceite.'}
                      </p>
                    </section>
                  )}
                </div>
              )}
            </div>
            {ajuda && <AjudaDoPasso titulo={ajuda.titulo}>{ajuda.corpo(contexto)}</AjudaDoPasso>}
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            {passo > 0 && (
              <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
                <Icone nome="voltar" tamanho={14} /> Voltar
              </button>
            )}
            <Progresso atual={passo} total={passos.length} />
            {passo < ULTIMO && (
              <button type="button" className={`imv-bt ${editando ? 'fantasma' : 'primario'}`} onClick={avancar}>
                Avançar <Icone nome="avancar" tamanho={14} />
              </button>
            )}
            {(editando || passo === ULTIMO) && (
              <button type="button" className="imv-bt primario" disabled={salvando} onClick={pedirParaGravar}>
                <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Gravar proposta'}
              </button>
            )}
          </footer>
        </section>
      </div>

      {/* Apagar sempre pergunta — a regra dele, 18/09/2026. Aqui nada sai do
          banco até gravar, mas o que se escreveu na pesquisa sai da tela. */}
      {removendo !== null && pesquisas[removendo] && (
        <Confirmar
          titulo={`Remover a pesquisa ${removendo + 1}`}
          descricao={`A pesquisa ${NIVEIS[pesquisas[removendo].nivel].longo} sai ${editando ? `${ehProposta ? 'da proposta' : 'do contrato'} ao salvar` : 'da proposta'}, com o honorário e os investigados escritos nela.`}
          rotuloAcao="Remover"
          aoFechar={() => setRemovendo(null)}
          aoConfirmar={() => {
            const pos = removendo;
            setErro(null);
            setPesquisas((a) => a.filter((_, i) => i !== pos));
          }}
        />
      )}

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={editando ? `${ehProposta ? 'a proposta' : 'o contrato'} ${inicial.codigo}` : 'a proposta'}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}

/**
 * A linha sob os níveis: o que a cláusula daquele nível promete.
 *
 * ⚠️ **Os números vêm do servidor** (`/api/contratos/referencia`, que lê
 * `lib/niveis.ts`): são cláusula de contrato, e escritos aqui envelheceriam no
 * dia em que o portfólio mudasse. ⚠️ O Customizado não tem número: escopo
 * definido após briefing.
 */
function LinhaDaReferencia({ nivel, r }: { nivel: NivelServico; r: ReferenciaDoNivel | undefined }) {
  if (nivel === 'customizado' || !r || r.investigados === null) {
    return <>Escopo definido após briefing: sem teto nem honorário de referência.</>;
  }
  return (
    <>
      <b>{NIVEIS[nivel].verbo}</b> · até {plural(r.investigados, 'investigado', 'investigados')}
      {r.dias !== null && ` · ${r.dias} dias`}
      {r.reunioes !== null && ` · ${plural(r.reunioes, 'reunião', 'reuniões')}`}
      {r.honorarios && (
        <>
          {' '}
          · referência <Valor de={r.honorarios} />
        </>
      )}
    </>
  );
}

type Contexto = { ehProposta: boolean; ativo: boolean; cortesia: boolean; doPedido: boolean };

/** A coluna da direita, passo a passo. Texto de tela: sem fonte, sem ferramenta. */
const AJUDA: Record<'pesquisas' | 'investigados' | 'condicoes' | 'casos', { titulo: string; corpo: (c: Contexto) => ReactNode }> = {
  pesquisas: {
    titulo: 'Como funciona',
    corpo: (c) => (
      <>
        <p>Uma linha por pesquisa vendida: uma Insight e uma Kairós no mesmo contrato são duas pesquisas.</p>
        {c.cortesia ? (
          <p>
            <b>Cortesia:</b> o honorário some e o total fica em zero. O Fundo de Custas continua, pela tabela do nível, e o nível vale para o caso.
          </p>
        ) : c.ativo ? (
          <p>
            Contrato já aceito: trocar o nível <b>não</b> repõe o honorário, porque as cobranças já somam com o total.
          </p>
        ) : (
          <p>
            Escolher o nível preenche o honorário com o <b>valor de referência</b> — e troca o que estava digitado, porque mudar de nível é mudar de serviço.
          </p>
        )}
        <p>O total do contrato é a soma das pesquisas: não se digita.</p>
      </>
    ),
  },
  investigados: {
    titulo: 'Quem se investiga',
    corpo: (c) => (
      <>
        <p>
          A proposta vale para estas pessoas, e não para outras. Com duas pesquisas, <b>cada uma diz as suas</b>: o cliente lê quem cada uma cobre.
        </p>
        {c.doPedido && <p>A barra roxa marca o que o cliente escreveu no pedido. Mexeu no texto, a marca sai.</p>}
        {!c.ehProposta && <p>No contrato já aceito, o campo pode ficar como está.</p>}
      </>
    ),
  },
  condicoes: {
    titulo: 'Condições',
    corpo: (c) =>
      c.ativo ? (
        <p>
          <b>Honorário e Fundo de Custas estão travados</b>: as cobranças deste contrato já existem e somam com eles. Para alterar, use <b>Corrigir cobranças</b>{' '}
          no cartão do contrato.
        </p>
      ) : (
        <>
          <p>
            Proposta não tem cobrança: <b>forma de pagamento e vencimentos nascem no Aceitar</b>. Marcá-los agora seria inventar data de um negócio que talvez não
            feche.
          </p>
          <p>O Fundo de Custas é o desembolso em nome do cliente — emolumento, certidão. Não é honorário{c.cortesia ? ', e não entra na cortesia' : ''}.</p>
        </>
      ),
  },
  casos: {
    titulo: 'Casos',
    corpo: () => (
      <>
        <p>Um contrato pode cobrir vários casos: venda avulsa, pacote ou avença.</p>
        <p>
          Marcar transfere; desmarcar solta. <b>Desvincular nunca apaga o caso</b> — caso é trabalho.
        </p>
      </>
    ),
  },
};
